import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Sun,
  Moon,
  ArrowRight,
  ShieldCheck,
  GitMerge,
  Brain,
  Radio,
  Network,
  Settings2,
  Bell,
  Activity,
  Wrench,
  Plug,
  BookOpen,
  BarChart3,
  FileText,
  Database,
  MessageSquare,
} from 'lucide-react';

const WORKFLOWS = [
  { icon: Radio,    label: 'RAN operations & optimisation' },
  { icon: Network,  label: 'Core network (5GC / EPC / IMS)' },
  { icon: Settings2,label: 'Network integration' },
  { icon: Bell,     label: 'Managed services / NOC' },
  { icon: Activity, label: 'Deployment & commissioning' },
  { icon: Wrench,   label: 'RCA & troubleshooting' },
];

const CONNECTORS = [
  { icon: FileText,     label: 'OEM documentation' },
  { icon: BookOpen,     label: 'Internal SOPs & runbooks' },
  { icon: Database,     label: 'ITSM tickets & postmortems' },
  { icon: MessageSquare,label: 'SME corrections & reviews' },
  { icon: Plug,         label: 'SharePoint / Confluence' },
  { icon: BarChart3,    label: 'Performance & alarm data' },
];

const PILLARS = [
  {
    icon: GitMerge,
    title: 'Knowledge orchestration',
    body:
      'QueryVerse connects your OEM documentation, internal SOPs, ITSM tickets, runbooks, postmortems, and SME expertise into a unified operational context. The value is in connecting them — not owning them.',
  },
  {
    icon: Brain,
    title: 'Operational reasoning, not retrieval',
    body:
      'QueryVerse correlates operational evidence, procedures, historical incidents, and organisational knowledge to guide engineers through complex troubleshooting. Every answer is grounded in your verified procedures — not generic AI training data.',
  },
  {
    icon: ShieldCheck,
    title: 'Measurable outcomes',
    body:
      'QueryVerse measures MTTR, escalation rates, and knowledge reuse before and after deployment. At week six, your pilot produces a quantified business impact report — not a satisfaction survey.',
  },
];

export default function LandingPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="landing-page">
      {/* Nav */}
      <nav className="px-6 py-4 flex items-center justify-between relative z-50">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src="/queryverse-logo.png"
            alt="QueryVerse"
            className="h-10"
          />
          <span className="text-[10px] text-muted-foreground hidden sm:block italic">
            a product by AppCenn Pvt Ltd
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <Link
            to="/pricing"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:block"
            data-testid="nav-pricing"
          >
            Pricing
          </Link>
          <a
            href="#workflows"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:block"
            data-testid="nav-workflows"
          >
            Workflows
          </a>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          {user ? (
            <Button
              onClick={() => navigate('/chat')}
              className="rounded-full px-5 h-9 text-sm font-medium"
              data-testid="dashboard-btn"
            >
              Dashboard
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate('/auth')}
                className="rounded-full px-4 h-9 text-sm font-medium"
                data-testid="login-btn"
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate('/request-access')}
                className="rounded-full px-5 h-9 text-sm font-medium"
                data-testid="request-access-btn"
              >
                Request access
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 pt-16 pb-16">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 mb-8" data-testid="hero-eyebrow">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-sm font-medium text-primary">
              Enterprise telecom operations intelligence
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05] mb-6" data-testid="hero-headline">
            <span className="gradient-text">Connect your operational knowledge.</span>
            <br />
            <span className="text-foreground">Prove the outcome.</span>
          </h1>

          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-10" data-testid="hero-subheadline">
            QueryVerse connects your organisation's existing knowledge — OEM documentation,
            internal SOPs, incident history, and SME expertise — into a unified intelligence
            layer that guides engineers, captures learning, and measures operational improvement.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate('/request-access')}
              className="rounded-full px-8 h-12 text-base font-medium gap-2"
              data-testid="hero-request-access-btn"
            >
              Request access <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/auth')}
              className="rounded-full px-8 h-12 text-base font-medium"
              data-testid="hero-signin-btn"
            >
              Sign in
            </Button>
          </div>

          <p className="text-sm text-muted-foreground mt-6">
            Bring Your Own Knowledge · Tenant-isolated · Measurable outcomes
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="px-6 py-12 border-t border-border">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pillars">
          {PILLARS.map(function (p) {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="rounded-2xl border border-border bg-card p-6"
                data-testid={`pillar-${p.title.split(' ')[0].toLowerCase()}`}
              >
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Workflows */}
      <section id="workflows" className="px-6 py-16 border-t border-border">
        <div className="max-w-6xl mx-auto space-y-10">
          <div>
            <h2 className="text-base font-medium text-muted-foreground mb-6">Built for telecom operations</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="workflows-grid">
              {WORKFLOWS.map(function (w) {
                const Icon = w.icon;
                return (
                  <div
                    key={w.label}
                    className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-4"
                    data-testid={`workflow-${w.label.split(' ')[0].toLowerCase()}`}
                  >
                    <Icon className="h-5 w-5 text-primary shrink-0" />
                    <span className="text-sm font-medium">{w.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Knowledge Connectors */}
          <div>
            <h2 className="text-base font-medium text-muted-foreground mb-2">Knowledge Connectors</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
              QueryVerse is designed to securely ingest and reason over knowledge sources your organisation already owns or is licensed to use. You bring the knowledge — QueryVerse structures, connects, and reasons over it.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {CONNECTORS.map(function (c) {
                const Icon = c.icon;
                return (
                  <div key={c.label} className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-card/50 px-4 py-4">
                    <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                    <span className="text-sm text-muted-foreground">{c.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why different */}
      <section className="px-6 py-16 border-t border-border bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-base font-medium text-muted-foreground mb-2">What makes a QueryVerse answer different</h2>
          <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
            Both answers below use the same underlying question. The difference is whether the AI is reasoning over your organisation's verified operational knowledge — or guessing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-background p-6" data-testid="example-generic">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Generic AI assistant
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                "To configure the cell, you should adjust parameters as required and monitor performance.
                Tune the configuration based on network requirements and make further adjustments as necessary."
              </p>
            </div>
            <div className="rounded-2xl border border-primary/30 bg-background p-6" data-testid="example-queryverse">
              <p className="text-xs uppercase tracking-wider text-primary font-medium mb-3">QueryVerse — grounded in your knowledge</p>
              <p className="text-sm leading-relaxed">
                <strong>Configuration steps</strong> [Source: Your NR Commissioning SOP v3.1, p.42]
                <br />
                1. Set PCI in 0–1007 avoiding Tier-1/2 collision <em>[Source: NR Commissioning SOP]</em>
                <br />
                2. NR-ARFCN per n78 plan: 620 000–653 333 <em>[Inferred — Your NR RF Planning Standard]</em>
                <br />
                3. SSB power offset −3 dB; RS power per cell-radius bucket <em>[Source: Your RF Parameters Reference]</em>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Posture */}
      <section className="px-6 py-12 border-t border-border">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-6 text-sm" data-testid="posture">
          <div>
            <h3 className="font-semibold mb-1">Bring Your Own Knowledge</h3>
            <p className="text-muted-foreground">
              QueryVerse indexes and reasons over knowledge sources you already own or are licensed to use.
              We never claim ownership of your content.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">Tenant-isolated</h3>
            <p className="text-muted-foreground">
              Dedicated knowledge index per customer. No cross-operator data leakage.
              Your knowledge is never used to answer another organisation's questions.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">Audit-logged</h3>
            <p className="text-muted-foreground">
              Every ingestion, query, and admin action is recorded.
              Full event trail for compliance and governance requirements.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">Outcome-measured</h3>
            <p className="text-muted-foreground">
              MTTR, escalation rate, and knowledge reuse tracked from day one.
              Your pilot produces evidence, not anecdotes.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-16 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-semibold mb-4">
            Your engineers solve problems every day.<br />
            <span className="text-muted-foreground font-normal">QueryVerse ensures that knowledge stays.</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            QueryVerse does not replace your engineers. It captures how your best engineers solve problems,
            guides every engineer through the same proven workflow, and proves the operational impact
            with measurable outcomes.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Pilot access is invitation-based. Submit a short request — we'll review and reach out within 48 hours.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/request-access')}
            className="rounded-full px-8 h-12 text-base font-medium gap-2"
            data-testid="footer-request-access-btn"
          >
            Request access <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="px-6 py-6 text-center border-t border-border">
        <p className="text-xs text-muted-foreground">
          © QueryVerse — a product by AppCenn Pvt Ltd. By using QueryVerse, you agree
          to our <Link to="/terms" className="underline hover:text-foreground">Terms</Link> and{' '}
          <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
        </p>
      </footer>
    </div>
  );
}
